/**
 * Debounces and coalesces per-email contact lookups into batched requests, so a screen
 * rendering many UserNameWithEmail rows issues one call instead of one per row.
 */
// Request batching manager to prevent duplicate API calls.
// Consumers go through UserNameWithEmail; this is the shared queue behind it.
export class ContactRequestBatcher {
	private pendingEmails: Set<string> = new Set();
	private pendingCallbacks: Map<string, Set<() => void>> = new Map();
	private debounceTimer: NodeJS.Timeout | null = null;
	private readonly DEBOUNCE_MS = 100;
	private isProcessing = false;

	constructor(
		private getContactsFn: (emails: string[]) => Promise<any>,
		private userType: string
	) {}

	requestContact(email: string, callback: () => void) {
		// If already in pending, just add callback
		if (this.pendingEmails.has(email)) {
			if (!this.pendingCallbacks.has(email)) {
				this.pendingCallbacks.set(email, new Set());
			}
			this.pendingCallbacks.get(email).add(callback);
			return;
		}

		// Add to pending set
		this.pendingEmails.add(email);
		if (!this.pendingCallbacks.has(email)) {
			this.pendingCallbacks.set(email, new Set());
		}
		this.pendingCallbacks.get(email).add(callback);

		this.scheduleBatch();
	}

	// Schedules the next drain, unless one is already pending.
	private scheduleBatch(delayMs: number = this.DEBOUNCE_MS) {
		if (this.debounceTimer) {
			clearTimeout(this.debounceTimer);
		}
		this.debounceTimer = setTimeout(() => this.processBatch(), delayMs);
	}

	private async processBatch() {
		// The timer has fired; drop the handle so a later requestContact schedules a fresh one
		// rather than clearing an already-fired timeout and believing one is still pending.
		this.debounceTimer = null;

		if (this.pendingEmails.size === 0) {
			return;
		}

		// A request is already in flight, so these emails wait for it rather than being
		// abandoned. Returning without rescheduling used to strand them in pendingEmails
		// forever: requestContact short-circuits for an email already pending, so nothing
		// would ever schedule another drain and every row in that wave rendered blank.
		// This reschedule is the guarantee that the queue is never dropped; the drain in
		// the finally block below is what makes it prompt rather than a debounce later.
		if (this.isProcessing) {
			this.scheduleBatch();
			return;
		}

		this.isProcessing = true;
		const emailsToFetch = Array.from(this.pendingEmails);
		const allCallbacks = new Set<() => void>();

		// Collect all callbacks
		this.pendingCallbacks.forEach((callbacks) => {
			callbacks.forEach((cb) => allCallbacks.add(cb));
		});

		// Clear pending state
		this.pendingEmails.clear();
		this.pendingCallbacks.clear();

		try {
			await this.getContactsFn(emailsToFetch);
		} catch (error) {
			console.error('Error fetching contacts:', error);
		} finally {
			// Execute all callbacks
			allCallbacks.forEach((callback) => {
				try {
					callback();
				} catch (error) {
					console.error('Error in contact fetch callback:', error);
				}
			});
			this.isProcessing = false;

			// Emails that arrived while this request was in flight are waiting on a drain that
			// bailed out above. Now that the lane is clear, run it.
			if (this.pendingEmails.size > 0) {
				this.scheduleBatch(0);
			}
		}
	}
}
